import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '@/models/user.ts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Building2, Crown, User as UserIcon, Users } from 'lucide-react';

interface HierarchyChartProps {
    users: User[];
}

interface HierarchyNode {
    user: User;
    children: HierarchyNode[];
    level: number;
}

const HierarchyChart: React.FC<HierarchyChartProps> = ({ users }) => {
    const navigate = useNavigate();

    const hierarchy = useMemo(() => {
        // Find root users (users without supervisors)
        const rootUsers = users.filter(user => !user.supervisor_id);

        // Build hierarchy tree
        const buildHierarchy = (user: User, level: number = 0): HierarchyNode => {
            const children = users
                .filter(u => u.supervisor_id === user.id)
                .map(child => buildHierarchy(child, level + 1));

            return {
                user,
                children,
                level,
            };
        };

        return rootUsers.map(user => buildHierarchy(user));
    }, [users]);

    const getStatusColor = (status: User['status']) => {
        switch (status) {
            case 'active':
                return 'bg-green-100 text-green-800 border-green-300';
            case 'inactive':
                return 'bg-yellow-100 text-yellow-800 border-yellow-300';
            case 'suspended':
                return 'bg-red-100 text-red-800 border-red-300';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-300';
        }
    };

    const getTitleIcon = (title?: string) => {
        switch (title?.toLowerCase()) {
            case 'manager':
            case 'director':
                return <Crown className="h-4 w-4 text-yellow-600" />;
            case 'sales':
                return <Building2 className="h-4 w-4 text-blue-600" />;
            case 'support':
                return <Users className="h-4 w-4 text-green-600" />;
            case 'retention':
                return <UserIcon className="h-4 w-4 text-purple-600" />;
            default:
                return <UserIcon className="h-4 w-4 text-gray-600" />;
        }
    };

    const renderNode = (node: HierarchyNode) => {
        const { user, children, level } = node;

        return (
            <div key={user.id} className="flex flex-col items-center">
                {/* Employee Card */}
                <Card
                    className={`w-64 mb-4 cursor-pointer transition-all hover:shadow-lg hover:scale-105 ${level === 0 ? 'border-2 border-blue-300 bg-blue-50' : ''}`}
                    onClick={() => navigate(`/users/${user.id}/edit`)}
                >
                    <CardHeader className="pb-2">
                        <CardTitle className="flex items-center gap-2 text-sm">
                            {getTitleIcon(user.title)}
                            <span className="truncate">{user.name}</span>
                            <span className="text-xs text-muted-foreground ml-auto">
                                Click to edit
                            </span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                        <div className="space-y-2">
                            {user.title && (
                                <div className="text-xs text-muted-foreground">{user.title}</div>
                            )}
                            <div className="flex items-center gap-2">
                                <Badge className={getStatusColor(user.status)}>
                                    {user.status === 'active'
                                        ? 'Active'
                                        : user.status === 'suspended'
                                          ? 'Suspended'
                                          : 'Inactive'}
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                    {user.role?.name || 'User'}
                                </Badge>
                            </div>
                            {user.subordinates_count && user.subordinates_count > 0 ? (
                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                    <Users className="h-3 w-3" />
                                    <span>{user.subordinates_count} team members</span>
                                </div>
                            ) : (
                                ''
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Children */}
                {children.length > 0 && (
                    <div className="flex gap-4">
                        {children.map(child => (
                            <div key={child.user.id} className="flex flex-col items-center">
                                {/* Connection Line */}
                                <div className="w-px h-4 bg-gray-300 mb-2"></div>
                                {renderNode(child)}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    if (hierarchy.length === 0) {
        return (
            <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                    <Users className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No Hierarchy Data</h3>
                    <p className="text-muted-foreground text-center">
                        No employees found or no supervisor relationships configured.
                    </p>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-semibold">Organization Chart</h2>
                    <p className="text-sm text-muted-foreground">
                        Visual representation of employee hierarchy and reporting structure. Click
                        on any employee card to edit their details.
                    </p>
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-blue-300 rounded"></div>
                        <span>Top Level</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Crown className="h-3 w-3 text-yellow-600" />
                        <span>Management</span>
                    </div>
                </div>
            </div>

            <div className="overflow-x-auto">
                <div className="flex justify-center min-w-max">
                    {hierarchy.map(rootNode => (
                        <div key={rootNode.user.id} className="flex flex-col items-center">
                            {renderNode(rootNode)}
                        </div>
                    ))}
                </div>
            </div>

            {/* Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-2">
                            <Users className="h-5 w-5 text-blue-600" />
                            <div>
                                <div className="text-2xl font-bold">{users.length}</div>
                                <div className="text-sm text-muted-foreground">Total Employees</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-2">
                            <Crown className="h-5 w-5 text-yellow-600" />
                            <div>
                                <div className="text-2xl font-bold">
                                    {
                                        users.filter(
                                            u =>
                                                u.title?.toLowerCase().includes('manager') ||
                                                u.title?.toLowerCase().includes('director')
                                        ).length
                                    }
                                </div>
                                <div className="text-sm text-muted-foreground">Managers</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-2">
                            <Building2 className="h-5 w-5 text-green-600" />
                            <div>
                                <div className="text-2xl font-bold">
                                    {users.filter(u => u.status === 'active').length}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                    Active Employees
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default HierarchyChart;
